import { useEffect, useRef } from 'react';
import { SCAN_SERVICE_STATUS } from '@witmetrics/utils/constants';
import { mapToKey } from '@witmetrics/utils/arrays';
import {
  useActivePractice,
  useDispatch,
  useScanServicesByStatus,
  useScanServicesFromIdArray,
} from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { closeToaster, showToaster, type ToastId } from '@/utils/toasters';
import { useIds } from '@/hooks/useIds';
import IconButton from '@/components/IconButton';
import { ArchiveIcon } from '@/icons';
import Badge from '@/components/Badge';
import PendingServicesMenu from '@/menus/PendingServicesMenu';
import Tooltip from '@/components/Tooltip';
import ScanServiceToaster from '@/toasters/ScanServiceToaster';
import {
  classes,
  fetchPendingServices,
  getUpdatedServiceIds,
  sortServices,
  styles,
} from './utils';

const { IN_PROGRESS } = SCAN_SERVICE_STATUS;

export type NotificationsProps = {
  className?: string;
};

export default function Notifications({ className }: NotificationsProps) {
  const menuToggleRef = useRef<HTMLDivElement>(null);
  const openToastersRef = useRef<Record<number, ToastId>>({});
  const dispatch = useDispatch();
  const { onApiError, onSelectService } = useAppState();
  const activePractice = useActivePractice();
  const pendingServices = useScanServicesByStatus(IN_PROGRESS);
  const [
    serviceIds,
    setServiceIds,
    { addIds: addServiceIds, removeId: removeServiceId },
  ] = useIds(); // Initial list of pending services
  const services = useScanServicesFromIdArray(serviceIds);
  const [isMenuOpen, toggleMenu] = useToggle();

  useEffect(() => {
    if (activePractice) fetchData();
  }, [activePractice]);

  useEffect(() => {
    checkNewServices();
  }, [pendingServices]);

  useEffect(() => {
    checkUpdatedServices();
  }, [services]);

  const fetchData = async () => {
    // eslint-disable-next-line witmetrics/no-multiline-comments
    /* When the page first loads, fetch all currently pending services
      When a new service starts, add it to the list
      When a service is updated/completed, keep it in the list & show a toaster
     */
    try {
      if (activePractice) {
        const results = await fetchPendingServices(activePractice.id);
        dispatch.addProjectScanServices(results);
        setServiceIds(sortServices(results));
      }
    } catch (err) {
      onApiError(err, 'Error fetching services', () => fetchData());
    }
  };

  const checkNewServices = () => {
    addServiceIds(mapToKey(pendingServices, 'id'));
  };

  const checkUpdatedServices = () => {
    // TODO: Refactor this to limit excessive renderings
    const updatedIds = getUpdatedServiceIds(services);
    if (updatedIds.length > 0) {
      updatedIds.forEach((serviceId) => {
        openToastersRef.current[serviceId] = showToaster(
          renderToaster(serviceId),
          {
            autoClose: false,
            position: 'top-right',
            className: classes.toaster,
            style: styles.toaster,
            bodyStyle: styles.toasterBody,
          }
        );
      });
    }
  };

  const handleCloseService = (serviceId: number) => {
    removeServiceId(serviceId);
    closeToaster(openToastersRef.current[serviceId]);
  };

  const handleOpenService = (serviceId: number) => {
    onSelectService(serviceId);
    handleCloseService(serviceId);
  };

  const renderToaster = (serviceId: number) => {
    return (
      <ScanServiceToaster
        serviceId={serviceId}
        onOpen={handleOpenService}
        onClose={() => handleCloseService(serviceId)}
      />
    );
  };

  const count = pendingServices.length;

  return (
    <>
      {isMenuOpen && (
        <PendingServicesMenu
          anchorTo={menuToggleRef.current}
          open
          serviceIds={serviceIds}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={() => toggleMenu()}
        />
      )}
      <div ref={menuToggleRef}>
        <Tooltip
          arrow
          enterDelay={0}
          placement="bottom"
          title="Pending Services">
          <IconButton className={className} onClick={() => toggleMenu()}>
            <Badge badgeContent={count} color="orange-3">
              <ArchiveIcon
                style={count > 0 ? styles.active : {}}
                className={classes.icon}
              />
            </Badge>
          </IconButton>
        </Tooltip>
      </div>
    </>
  );
}
